import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, MGMT_LABEL, pretty } from "@/lib/api";

const FIELDS = [
  ["name", "Legal name"], ["legal_form", "Legal form"], ["registration_number", "BRELA registration no."], ["tin", "TIN"],
  ["vrn", "VRN"], ["incorporation_date", "Incorporation date", "date"], ["sector", "Sector"], ["region", "Region"],
  ["address", "Address"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"],
  ["employee_count", "Employees", "number"], ["turnover_band", "Turnover band"], ["financial_year_end", "Financial year end (MM-DD)"],
];

export function ReassessBanner({ company, onDone }) {
  const { isStaff } = useAuth();
  const [note, setNote] = useState("");
  if (!company.reassessment_required) return null;
  const resolve = async () => {
    try { await api.post(`/companies/${company.id}/reassessment/resolve`, { note }); toast.success("Reassessment resolved"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="reassessment-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-semibold text-amber-900">Reassessment required — profile changed: {(company.reassessment_fields || []).map(pretty).join(", ")}</p>
          <p className="text-xs text-amber-800">No obligations were changed automatically. {isStaff ? "Review applicability, then record your conclusion." : "V.A will review your obligations."}</p>
          {isStaff && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input placeholder="Reassessment conclusion" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" data-testid="reassessment-note-input" />
              <Button onClick={resolve} disabled={note.length < 3} data-testid="reassessment-resolve-btn">Mark reviewed</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CompanyProfile({ company, onSaved }) {
  const { user, isStaff } = useAuth();
  const canEdit = isStaff || user.role === "client_owner";
  const [f, setF] = useState(company);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = Object.fromEntries(FIELDS.map(([k, , t]) => [k, t === "number" ? (f[k] === "" || f[k] == null ? null : Number(f[k])) : f[k] || null]));
    if (isStaff) { body.management_model = f.management_model; body.notes = f.notes || null; }
    try { const { data } = await api.put(`/companies/${company.id}`, body); toast.success(data.reassessment_required ? "Saved · reassessment flagged for V.A" : "Profile saved"); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border bg-card p-5 sm:p-6" data-testid="company-profile-form">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FIELDS.map(([k, label, type]) => (
          <Field key={k} label={label}><Input type={type || "text"} value={f[k] ?? ""} onChange={set(k)} disabled={!canEdit} data-testid={`company-field-${k}`} /></Field>
        ))}
        <Field label="Management model">
          <Select value={f.management_model} onValueChange={(v) => setF({ ...f, management_model: v })} disabled={!isStaff}>
            <SelectTrigger data-testid="company-field-management_model"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(MGMT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      {isStaff && <div className="mt-4"><Field label="Internal notes (staff only)"><Textarea value={f.notes || ""} onChange={set("notes")} data-testid="company-field-notes" /></Field></div>}
      {canEdit && <div className="mt-6 flex justify-end"><Button onClick={save} disabled={busy} data-testid="company-save-btn">{busy ? "Saving…" : "Save profile"}</Button></div>}
      <p className="mt-3 text-xs text-muted-foreground">Changes to legal form, identifiers, sector, size or management model flag the company for staff reassessment.</p>
    </div>
  );
}
